import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, ButtonBase, Popover, Stack, Tooltip, Typography } from '@mui/material';
// hooks
import useAuth from 'src/hooks/useAuth';
// components
import MyAvatar from 'src/components/MyAvatar';
import Icon from 'src/components/universe/Icon';
import {
  BRAND_ICON_WHITE,
  BRAND_LOGO_WHITE,
  MOTION,
  RADIUS,
  UNIVERSE,
  UNIVERSE_FONT
} from 'src/theme/universe/brand';
import { menuPaperSx } from 'src/theme/universe/surfaceSx';
import NavbarPortalCard from '../dashboard/navbar/NavbarPortalCard';
import { sidebarBackground, sidebarVars } from '../dashboard/navbar/sidebarTokens';
import { AUTH_PORTALS } from '../auth/portals';
//
import VendorAccountPanel, { accountName } from './VendorAccountMenu';
import { ROLE_WORD, homeFor, isActive, navGroupsFor } from './vendorNav';

// ---------------------------------------------------------------------------
// THE VENDOR RAIL (DESIGN.md §12): the night the whole product is known by,
// down the left of every page from 900px.
//
// Two widths. EXPANDED (264px, the default from 1440) is the sidebar vendors
// already know — the white lock-up, the destinations as labelled rows, the
// star at the foot and the account under it — with the destinations put in
// named groups once a type has more than three. COMPACT (88px, below 1440,
// or collapsed by the reader) is the §12 rail: a 36px tile per destination
// with its word UNDER it, because a tooltip does not exist on the touch
// screen a 1024px tablet usually is (DECISION 1).
//
// The light is the Universe's own — the brand's light blue as the beacon, the
// gradient on the active tile — on every portal (DECISION 3, as the vendor
// sign-in already wears it). The violet the vendor rail carried before was
// the Group palette §12 retired from the rail.
// ---------------------------------------------------------------------------

/** The rail lit the Universe's way, whatever portal it serves. */
const RAIL_PORTAL = {
  ...AUTH_PORTALS.vendor,
  accent: UNIVERSE.mid,
  accentBright: UNIVERSE.light,
  accentDeep: UNIVERSE.navy,
  gradient: UNIVERSE.gradient
};

export const RAIL_WIDTH = { expanded: 264, compact: 88 };

const WHITE_SOFT = alpha(UNIVERSE.white, 0.7);
const WHITE_QUIET = alpha(UNIVERSE.white, 0.46);

const ringSx = {
  outline: 'none',
  boxShadow: `0 0 0 2px ${UNIVERSE.night}, 0 0 0 4px ${UNIVERSE.light}`
};

// ---------------------------------------------------------------------------
// ONE DESTINATION

RailItem.propTypes = {
  item: PropTypes.object.isRequired,
  active: PropTypes.bool,
  compact: PropTypes.bool
};

function RailItem({ item, active, compact }) {
  const tile = (
    <Box
      className="vr-tile"
      sx={{
        width: compact ? 44 : 36,
        height: 36,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: `${RADIUS.small}px`,
        color: active ? UNIVERSE.white : WHITE_SOFT,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        border: '1px solid',
        borderColor: 'transparent',
        boxShadow: 'none',
        transition: `color ${MOTION.fast}ms`
      }}
    >
      <Icon name={item.icon} size="lg" variant={active ? 'Bold' : 'Linear'} />
    </Box>
  );

  return (
    <ButtonBase
      component={RouterLink}
      to={item.path}
      aria-current={active ? 'page' : undefined}
      aria-label={compact ? item.label : undefined}
      sx={{
        position: 'relative',
        width: 1,
        display: 'flex',
        textDecoration: 'none',
        borderRadius: `${RADIUS.overlay}px`,
        color: active ? UNIVERSE.white : WHITE_SOFT,
        ...(compact
          ? {
              flexDirection: 'column',
              gap: 0.5,
              py: 0.75,
              minHeight: 64
            }
          : {
              justifyContent: 'flex-start',
              gap: 1.5,
              minHeight: 50,
              pl: 1,
              pr: 1.5,
              ...(active && {
                bgcolor: 'rgba(255,255,255,0.04)',
                backgroundImage: `linear-gradient(90deg, ${alpha(
                  UNIVERSE.light,
                  0.18
                )} 0%, rgba(255,255,255,0) 72%)`
              })
            }),
        // the beacon: "you are here" on the rail's own edge
        '&::before': {
          content: '""',
          position: 'absolute',
          left: compact ? -16 : -14,
          top: '50%',
          width: 3,
          height: active ? 22 : 0,
          borderRadius: 3,
          transform: 'translateY(-50%)',
          bgcolor: UNIVERSE.light,
          boxShadow: `0 0 14px ${alpha(UNIVERSE.light, 0.45)}`,
          transition: `height ${MOTION.standard}ms`
        },
        // an instant fill under the pointer (an animated translucent fill over
        // the night is what read as input lag on the intranet rail)
        '&:hover': {
          color: UNIVERSE.white,
          ...(!compact && !active && { bgcolor: 'rgba(255,255,255,0.05)' }),
          '& .vr-tile': {
            color: UNIVERSE.white,
            ...(!active && {
              bgcolor: 'rgba(255,255,255,0.1)',
              borderColor: 'rgba(255,255,255,0.16)'
            })
          }
        },
        '&.Mui-focusVisible, &:focus-visible': ringSx,
        '@media (prefers-reduced-motion: reduce)': {
          '&::before': { transition: 'none' }
        }
      }}
    >
      {tile}
      <Typography
        component="span"
        noWrap
        sx={{
          minWidth: 0,
          fontFamily: UNIVERSE_FONT,
          color: 'inherit',
          ...(compact
            ? {
                maxWidth: 1,
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: 0,
                textAlign: 'center'
              }
            : { fontSize: 15, fontWeight: active ? 600 : 500, lineHeight: 1.4 })
        }}
      >
        {compact ? item.short || item.label : item.label}
      </Typography>
    </ButtonBase>
  );
}

// ---------------------------------------------------------------------------

VendorRail.propTypes = {
  compact: PropTypes.bool,
  /** Shown from 1200, where the reader may collapse and expand the rail. */
  onToggle: PropTypes.func
};

export default function VendorRail({ compact = false, onToggle }) {
  const { pathname } = useLocation();
  const { user, type } = useAuth();
  const groups = navGroupsFor(type);
  const [anchor, setAnchor] = useState(null);
  const home = homeFor(type);
  const name = accountName(user);
  const grouped = groups.length > 1;

  return (
    <Box
      component="nav"
      aria-label="Vendor portal"
      sx={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 1200,
        width: compact ? RAIL_WIDTH.compact : RAIL_WIDTH.expanded,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        // the night of every portal's rail, with the Universe's own light
        ...sidebarVars(RAIL_PORTAL, 'dark'),
        ...sidebarBackground(RAIL_PORTAL, 'dark'),
        color: UNIVERSE.white,
        fontFamily: UNIVERSE_FONT,
        // in light mode 16.5:1 against the canvas carries the edge; on the
        // night a hairline is the only thing that does (§12)
        borderRight: (theme) =>
          theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : 0,
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255,255,255,0.12) transparent'
      }}
    >
      {/* the mark: this portal's home, never the portal chooser */}
      <Stack
        direction={compact ? 'column' : 'row'}
        alignItems="center"
        justifyContent={compact ? 'center' : 'space-between'}
        spacing={compact ? 1.25 : 1}
        sx={{ px: compact ? 1.5 : 2.5, pt: 2.75, pb: compact ? 1.5 : 2.5 }}
      >
        <Box
          component={RouterLink}
          to={home}
          aria-label="LingoYou Universe, home"
          sx={{
            display: 'inline-flex',
            lineHeight: 0,
            borderRadius: `${RADIUS.small}px`,
            '&:focus-visible': ringSx
          }}
        >
          <Box
            component="img"
            src={compact ? BRAND_ICON_WHITE : BRAND_LOGO_WHITE}
            alt="LingoYou Universe"
            sx={
              compact
                ? { width: 34, height: 34, objectFit: 'contain' }
                : { height: 36, width: 'auto', maxWidth: 176, display: 'block' }
            }
          />
        </Box>
        {onToggle && (
          <Tooltip arrow placement="right" title={compact ? 'Expand' : 'Collapse'}>
            <ButtonBase
              onClick={onToggle}
              aria-label={compact ? 'Expand the menu' : 'Collapse the menu'}
              sx={{
                width: 32,
                height: 32,
                borderRadius: `${RADIUS.button}px`,
                color: WHITE_QUIET,
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': { color: UNIVERSE.white, bgcolor: 'rgba(255,255,255,0.08)' },
                '&.Mui-focusVisible, &:focus-visible': ringSx
              }}
            >
              <Icon name={compact ? 'ArrowRight2' : 'ArrowLeft2'} size="sm" />
            </ButtonBase>
          </Tooltip>
        )}
      </Stack>

      {/* the destinations */}
      <Box sx={{ px: compact ? 1 : 2, pb: 2 }}>
        {groups.map((group, index) => (
          <Box
            key={group.group || index}
            component="section"
            aria-label={group.group || undefined}
            sx={{
              ...(index > 0 && {
                mt: compact ? 1.5 : 3,
                ...(compact && {
                  pt: 1,
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  mx: 1.5
                })
              })
            }}
          >
            {grouped && !compact && group.group && (
              <Typography
                component="h2"
                sx={{
                  px: 1.25,
                  pb: 0.75,
                  fontFamily: UNIVERSE_FONT,
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  letterSpacing: 0,
                  color: WHITE_QUIET
                }}
              >
                {group.group}
              </Typography>
            )}
            <Stack spacing={compact ? 0.5 : 0.5} sx={compact ? { mx: -1.5 } : undefined}>
              {group.items.map((item) => (
                <RailItem
                  key={item.key}
                  item={item}
                  compact={compact}
                  active={isActive(item, pathname)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {/* the star — a brand moment, kept where there is room for it */}
      {!compact && (
        <Box
          sx={{
            mx: -0.25,
            '@media (max-height: 760px)': { display: 'none' }
          }}
        >
          <NavbarPortalCard portal={RAIL_PORTAL} />
        </Box>
      )}

      {/* the account: who is signed in, the theme, signing out */}
      <Box sx={{ px: compact ? 1.25 : 2, pb: 2.5, pt: compact ? 1 : 0 }}>
        <ButtonBase
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          aria-label={`Account: ${name}`}
          sx={{
            width: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: compact ? 'center' : 'flex-start',
            gap: 1.5,
            p: compact ? 0.5 : 1.25,
            borderRadius: `${RADIUS.overlay}px`,
            textAlign: 'left',
            bgcolor: compact ? 'transparent' : 'rgba(255,255,255,0.05)',
            border: '1px solid',
            borderColor: compact ? 'transparent' : 'rgba(255,255,255,0.09)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)',
              borderColor: compact ? 'transparent' : 'rgba(255,255,255,0.16)'
            },
            '&.Mui-focusVisible, &:focus-visible': ringSx
          }}
        >
          {/* the gradient ring: light around the photo, not a border on it */}
          <Box
            sx={{
              flex: 'none',
              p: '2px',
              borderRadius: '50%',
              backgroundImage: UNIVERSE.gradient,
              boxShadow: `0 6px 18px ${alpha(UNIVERSE.light, 0.32)}`
            }}
          >
            <Box sx={{ p: '2px', borderRadius: '50%', bgcolor: UNIVERSE.night, lineHeight: 0 }}>
              <MyAvatar sx={{ width: 34, height: 34 }} />
            </Box>
          </Box>
          {!compact && (
            <>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography
                  noWrap
                  sx={{
                    fontFamily: UNIVERSE_FONT,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: UNIVERSE.white
                  }}
                >
                  {name}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    mt: 0.25,
                    fontFamily: UNIVERSE_FONT,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    color: WHITE_SOFT
                  }}
                >
                  {ROLE_WORD[type] || type}
                </Typography>
              </Box>
              <Icon name="ArrowUp2" size="sm" sx={{ color: WHITE_QUIET }} />
            </>
          )}
        </ButtonBase>
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: compact ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: (theme) => ({
            ...menuPaperSx(theme),
            width: 300,
            mt: -1,
            ml: compact ? 1 : 0,
            p: 1
          })
        }}
      >
        <VendorAccountPanel onClose={() => setAnchor(null)} />
      </Popover>
    </Box>
  );
}
